/**
 * Service to send emails using the configured delivery provider.
 */

import { authFetch } from '../api';

const RESEND_URL = import.meta.env.VITE_SUPABASE_RESEND_URL || 'https://poctrvtcrornrbonuzbr.supabase.co/functions/v1/resend-email';
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'sb_publishable_0C8_STSUB3b9g3oWKLfjZQ_92V5kWzf';
const EMAIL_API_URL = `${import.meta.env.VITE_API_URL || '/api'}/email`;

/**
 * Sends an email via the configured provider.
 * Learner invitations use NoCodeBackend's password-reset flow so the learner
 * receives a secure setup link from the same authentication provider.
 *
 * @param {Object} payload
 * @param {string | string[]} payload.to - Recipient email address(es)
 * @param {string} payload.toEmail - Recipient email address for server-side Resend delivery
 * @param {string} payload.toName - Recipient name for server-side Resend delivery
 * @param {string} payload.subject - Email subject
 * @param {string} payload.message - Plain-text email message for server-side Resend delivery
 * @param {string} payload.html - HTML content of the email
 * @param {string} [payload.from] - Optional sender email
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const sendEmail = async ({ to, toEmail, toName, subject, message, html, from }) => {
  if (toEmail && message) {
    try {
      const response = await fetch(RESEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${PUBLISHABLE_KEY}`,
          'apikey': PUBLISHABLE_KEY
        },
        body: JSON.stringify({
          from: from || import.meta.env.VITE_SMTP_FROM || 'noreply@sentinel.biscontractors.com',
          to: to || toEmail,
          toEmail,
          toName,
          subject,
          message,
          html: html || message
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        return {
          success: false,
          error: data?.message || data?.error || 'Failed to send email through Resend.'
        };
      }

      return {
        success: true,
        data: data?.data ?? data,
        message: data?.message
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to reach the Resend email service.'
      };
    }
  }

  const isLearnerInvite = subject === "You're Invited to Join the Security Awareness Platform";

  if (isLearnerInvite && false) {
    const email = Array.isArray(to) ? to[0] : to;

    const response = await authFetch('forget-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });

    if (response?.status === 'success') {
      return {
        success: true,
        data: response?.data
      };
    }

    return {
      success: false,
      error: response?.error || 'Failed to send the learner invitation email.'
    };
  }

  try {
    const sender = from || import.meta.env.VITE_SMTP_FROM || 'noreply@sentinel.biscontractors.com';

    const response = await fetch(RESEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${PUBLISHABLE_KEY}`,
        'apikey': PUBLISHABLE_KEY
      },
      body: JSON.stringify({
        from: sender,
        to,
        toEmail: toEmail || (Array.isArray(to) ? to[0] : to),
        toName,
        subject,
        message: message || html || subject,
        html
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error sending email via Supabase Edge Function:', error);
    return {
      success: false,
      error: error.message || 'Failed to send email'
    };
  }
};

/**
 * Sends a learner reminder through the configured Resend service.
 */
export const sendReminderEmail = async (payload) => sendEmail(payload);

/**
 * Sends reminder emails through the server-side Resend integration.
 * The API key remains on the server and is never exposed to the browser.
 */
export const sendBulkReminderEmails = async (recipients) => {
  try {
    const response = await fetch(RESEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${PUBLISHABLE_KEY}`,
        'apikey': PUBLISHABLE_KEY
      },
      body: JSON.stringify({ recipients })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok && response.status !== 207) {
      return {
        success: false,
        sent: 0,
        failed: recipients.length,
        results: [],
        error: data?.message || 'Unable to send reminder emails.'
      };
    }

    return {
      success: data?.success ?? false,
      sent: data?.sent ?? 0,
      failed: data?.failed ?? recipients.length,
      results: data?.results ?? []
    };
  } catch (error) {
    return {
      success: false,
      sent: 0,
      failed: recipients.length,
      results: [],
      error: error?.message || 'Unable to reach the email delivery service.'
    };
  }
};